/* eslint-disable */
import { RACE_CIRCUITS } from '@/constants/raceCircuits'
import { VirtualRaceSimulator } from '@/services/virtualRaceSimulator'
import { MEMORY_CONFIG } from '@/constants/memoryConfig'
import { PerformanceRecordsService } from '@/services/performanceRecordsService'

export class BackgroundRaceSimulator {
  static BASE_TIMES = {
    short: 43.2,    // Target time for perfect run
    medium: 105.0,
    long: 340.0
  }

  static SPEED_WEIGHTS = {
    short: 0.7,
    medium: 0.5,
    long: 0.3
  }

  static DAILY_RACE_LIMIT = MEMORY_CONFIG.backgroundRacesPerDay

  static getCircuitForLevel(level) {
    if (level >= 40) return RACE_CIRCUITS.SAUCER
    if (level >= 30) return RACE_CIRCUITS.GLOBAL
    if (level >= 20) return RACE_CIRCUITS.TRANS_CONTINENTAL
    if (level >= 10) return RACE_CIRCUITS.CONTINENTAL
    return RACE_CIRCUITS.REGIONAL
  }

  static generateRacePosition(chocobo, raceType) {
    const performanceScore = (
      (chocobo.speed * this.SPEED_WEIGHTS[raceType]) + 
      (chocobo.stamina * (1 - this.SPEED_WEIGHTS[raceType]))
    )

    // Better performance = better chance of good position
    const normalizedScore = Math.min(performanceScore / 250, 1)
    
    // Generate weighted random position
    const rand = Math.random()
    if (normalizedScore > 0.8 && rand < 0.4) return 1
    if (normalizedScore > 0.6 && rand < 0.6) return 2
    if (normalizedScore > 0.4 && rand < 0.8) return 3
    return Math.min(6, Math.floor(rand * 6) + 1)
  }

  static simulateBackgroundRaces(gameStore) {
    // Use virtual racing for large-scale background simulation
    if (MEMORY_CONFIG.enableVirtualRacing && this.DAILY_RACE_LIMIT > 10) {
      return this.simulateVirtualBackgroundRaces(gameStore)
    }
    
    // Fall back to original implementation for smaller simulations
    return this.simulateDetailedBackgroundRaces(gameStore)
  }

  static simulateVirtualBackgroundRaces(gameStore) {
    // Generate daily race schedule
    const schedule = VirtualRaceSimulator.generateDailyRaceSchedule(gameStore)
    
    // Batch simulate all races
    const results = VirtualRaceSimulator.simulateBatchRaces(schedule, gameStore)
    
    // Update chocobo records and owner finances
    VirtualRaceSimulator.updateChocoboRecords(results, gameStore)
    
    // Process financial rewards
    results.forEach(race => {
      race.results.forEach(result => {
        if (result.position <= 3) {
          const chocobo = gameStore.chocobos.find(c => c.id === result.chocoboId)
          const owner = gameStore.owners.find(o => o.chocoboIds.includes(result.chocoboId))
          
          if (owner && chocobo) {
            const baseReward = {
              1: 4000,
              2: 2000,
              3: 1000
            }[result.position]
            const earnings = Math.floor(baseReward * race.circuit.rewardMultiplier)
            owner.money += earnings
          }
        }
      })
    })
    
    // Process performance records for virtual background races
    this.processVirtualRacePerformanceRecords(results, gameStore)
    
    return results
  }

  static simulateDetailedBackgroundRaces(gameStore) {
    const results = []
    const raceTypes = ['short', 'medium', 'long']
    
    // Get all NPC chocobos (excluding those in transit or breeding)
    const allNPCChocobos = gameStore.owners
      .filter(owner => !owner.isPlayer)
      .flatMap(owner => gameStore.getChocobosByOwnerId(owner.id))
      .filter(chocobo => !chocobo.inTransit && !chocobo.isBreeding)
    
    // Randomly select chocobos for racing
    const selectedChocobos = this.selectRandomChocobos(allNPCChocobos, Math.min(this.DAILY_RACE_LIMIT, 10))
    
    selectedChocobos.forEach(chocobo => {
      const circuit = this.getCircuitForLevel(chocobo.level)
      const raceType = raceTypes[Math.floor(Math.random() * raceTypes.length)]
      const owner = gameStore.owners.find(o => o.chocoboIds.includes(chocobo.id))

      // Check if owner can afford entry fee
      if (!owner || owner.money < circuit.entryFee) {
        // Skip this race if owner can't afford it
        return
      }

      // Charge entry fee
      owner.money -= circuit.entryFee

      // Calculate raw finish time
      const finishTime = this.calculateFinishTime(chocobo, raceType)

      
      const position = this.generateRacePosition(chocobo, raceType)

      // Calculate earnings first
      let earnings = 0
      if (position <= 3) {
        const baseReward = {
          1: 4000,
          2: 2000,
          3: 1000
        }[position]
        earnings = Math.floor(baseReward * circuit.rewardMultiplier)
        owner.money += earnings
      }

      // Record race with explicit finish time and earnings
      chocobo.recordRaceResult(
        position,
        circuit,
        raceType,
        finishTime,
        gameStore.currentDay,
        earnings
      )

      // Store result
      results.push({
        chocobo,
        position,
        circuit,
        raceType,
        finishTime,
        owner,
        earnings
      })

      // Award experience
      const baseXP = position <= 3 ? 30 : 10
      const totalXP = Math.floor(baseXP * circuit.xpMultiplier)
      chocobo.gainExperience(totalXP)
    })

    // Process performance records for background races
    this.processBackgroundRacePerformanceRecords(results, gameStore)

    return results
  }

  static selectRandomChocobos(chocobos, count) {
    const shuffled = [...chocobos].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, Math.min(count, chocobos.length))
  }
  
  static calculateFinishTime(chocobo, raceType) {
    const baseTime = this.BASE_TIMES[raceType]
    const speedWeight = this.SPEED_WEIGHTS[raceType]
    const staminaWeight = 1 - speedWeight
  
    // Calculate performance score
    const performanceScore = (
      (chocobo.speed * speedWeight) + 
      (chocobo.stamina * staminaWeight)
    )
  
    // Normalize and calculate time
    const normalizedScore = Math.min(performanceScore / 250, 1)
    const variance = (1 - normalizedScore) * baseTime
    const randomFactor = 0.98 + (Math.random() * 0.04)
    const finalTime = (baseTime + variance) * randomFactor
  
    return finalTime
  }

  static getRaceTypeLabel(raceType) {
    return {
      short: '800m Sprint',
      medium: '2000m Classic',
      long: '5000m Endurance'
    }[raceType] || raceType
  }

  static processBackgroundRacePerformanceRecords(results, gameStore) {
    // Group results by race type to simulate realistic race participation
    const raceGroups = {}
    
    results.forEach(result => {
      const key = `${result.raceType}-${result.circuit.id}`
      if (!raceGroups[key]) {
        raceGroups[key] = []
      }
      raceGroups[key].push(result)
    })

    // Process each race group
    Object.entries(raceGroups).forEach(([key, raceResults]) => {
      const [raceType, circuitId] = key.split('-')
      const circuit = Object.values(RACE_CIRCUITS).find(c => c.id === circuitId)
      
      // Track used jockeys for this race to avoid duplicates within same race
      const usedJockeyIds = new Set()
      
      // Create participants with real jockeys for performance tracking
      const participants = raceResults.map(result => {
        const jockey = this.assignRandomJockey(gameStore, usedJockeyIds)
        
        // Update jockey's form based on race result
        if (jockey.updateForm) {
          jockey.updateForm(result.position)
        }
        
        return {
          chocobo: result.chocobo,
          jockey: jockey,
          finishTime: result.finishTime,
          formattedTime: this.formatTime(result.finishTime),
          speed: this.calculateSpeed(result.finishTime, raceType),
          position: result.position
        }
      })

      // Create race result object for performance tracking
      const raceResult = {
        circuit: circuit,
        raceType: raceType,
        participants: participants,
        currentDay: gameStore.currentDay
      }

      // Process performance records
      PerformanceRecordsService.processRaceResult(raceResult, gameStore)
    })
  }

  static processVirtualRacePerformanceRecords(virtualResults, gameStore) {
    // Process virtual race results for performance tracking
    virtualResults.forEach(race => {
      // Track used jockeys for this race to avoid duplicates within same race
      const usedJockeyIds = new Set()
      
      // Create participants from race results with real jockeys
      const participants = race.results.map(result => {
        const chocobo = gameStore.chocobos.find(c => c.id === result.chocoboId)
        if (!chocobo) return null
        
        const jockey = this.assignRandomJockey(gameStore, usedJockeyIds)
        
        // Update jockey's form based on race result
        if (jockey.updateForm) {
          jockey.updateForm(result.position)
        }
        
        return {
          chocobo: chocobo,
          jockey: jockey,
          finishTime: result.time,
          formattedTime: this.formatTime(result.time),
          speed: this.calculateSpeed(result.time, race.raceType),
          position: result.position
        }
      }).filter(p => p !== null) // Filter out any missing chocobos

      // Create race result object for performance tracking
      const raceResult = {
        circuit: race.circuit,
        raceType: race.raceType,
        participants: participants,
        currentDay: gameStore.currentDay
      }

      // Process performance records
      PerformanceRecordsService.processRaceResult(raceResult, gameStore)
    })
  }

  static assignRandomJockey(gameStore, usedJockeyIds = new Set()) {
    // Get available jockeys (not already used in this batch of races)
    const availableJockeys = gameStore.jockeys.filter(jockey => 
      !usedJockeyIds.has(jockey.id)
    )
    
    // If all jockeys are used, allow reuse (jockeys can ride multiple races per day)
    const jockeyPool = availableJockeys.length > 0 ? availableJockeys : gameStore.jockeys
    
    if (jockeyPool.length === 0) {
      // Fallback to mock jockey if no jockeys exist (shouldn't happen)
      return { id: 'background-jockey', name: 'Background Jockey' }
    }
    
    // Select random jockey
    const selectedJockey = jockeyPool[Math.floor(Math.random() * jockeyPool.length)]
    usedJockeyIds.add(selectedJockey.id)
    
    return selectedJockey
  }

  static formatTime(timeInSeconds) {
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    const milliseconds = Math.floor((timeInSeconds % 1) * 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
  }

  static calculateSpeed(timeInSeconds, raceType) {
    const distances = {
      short: 800,
      medium: 2000,
      long: 5000
    }
    const distance = distances[raceType] || 1000
    return `${(distance / timeInSeconds).toFixed(1)} m/s`
  }
}
